<template>
  <div>
    <Breadcrumbs :items="breadcrumbs" page_title="livestreaming" />

    <!-- Loading spinner -->
    <v-dialog v-model="loading" persistent width="700">
      <v-card style="background: transparent; box-shadow: unset">
        <v-card-text style="justify-content: center; display: flex">
          <v-progress-circular :size="70" :width="7" indeterminate color="#A6CC39"></v-progress-circular>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-card class="mx-3 my-5 rounded-lg" v-if="!loading">
      <v-container fluid>
        <v-row class="d-flex">
          <!-- Left: thumbnail -->
          <v-col cols="12" lg="6" md="6" sm="12" class="pt-5 pb-5 pl-5 pr-2">
            <v-img v-if="session.imageLink" :src="session.imageLink" class="detail-img rounded-lg"
              alt="session-thumbnail"></v-img>
            <div v-else class="detail-img-placeholder rounded-lg d-flex align-center justify-center">
              <v-icon size="80" color="#A6CC39">mdi-broadcast</v-icon>
            </div>
          </v-col>

          <!-- Right: detail info + action panel -->
          <v-col cols="12" lg="6" md="6" sm="12" class="pt-5 pr-5 pl-3">
            <!-- Status badge -->
            <v-chip small label class="font-weight-bold mb-3" :class="statusClass(session.status)">
              <v-icon x-small left>
                {{
                  session.status === 2
                    ? "mdi-circle"
                    : session.status === 0
                      ? "mdi-clock-outline"
                      : "mdi-check-circle-outline"
                }}
              </v-icon>
              {{ statusLabel(session.status) }}
            </v-chip>

            <!-- Title -->
            <v-card-title class="pa-0 mb-2"
              style="font-size: 22px; font-weight: bold; color: #363636; word-break: break-word">
              {{ session.title }}
            </v-card-title>

            <!-- Description -->
            <v-card-subtitle v-if="session.description" class="pa-0 mb-4"
              style="font-size: 15px; opacity: 0.8; line-height: 1.6; white-space: pre-line">
              {{ session.description }}
            </v-card-subtitle>

            <div class="d-flex align-center mb-2">
              <v-icon small color="#4fb14e" class="mr-2">mdi-calendar</v-icon>
              <span style="font-size: 15px; color: #4fb14e">{{ formatDate(session.scheduleDate) }}</span>
            </div>
            <div class="d-flex align-center mb-2">
              <v-icon small class="mr-2">mdi-clock-outline</v-icon>
              <span style="font-size: 15px; color: #777777">{{ formatTime(session.startTime) }} - {{
                formatTime(session.endTime) }}</span>
            </div>
            <div class="d-flex align-center mb-2" v-if="session.hostName">
              <v-icon small class="mr-2">mdi-account</v-icon>
              <span style="font-size: 15px; color: #777777">Host: {{ session.hostName }}</span>
            </div>
            <div class="d-flex align-center mb-4" v-if="session.coHostName">
              <v-icon small class="mr-2">mdi-account-multiple</v-icon>
              <span style="font-size: 15px; color: #777777">Co-Host: {{ session.coHostName }}</span>
            </div>

            <!-- Participant count -->
            <div v-if="session.noOfParticipants > 0" class="d-flex align-center mb-4">
              <v-icon small class="mr-2">mdi-account-group</v-icon>
              <span style="font-size: 15px; color: #777777">
                Participants: {{ session.joinedUsers }} / {{ session.noOfParticipants }}
              </span>
            </div>

            <v-divider class="mb-4"></v-divider>

            <!-- ═══════════════════════════════════════════════
                 ACTION PANEL — driven by session.status
            ════════════════════════════════════════════════ -->

            <!-- STATUS 0/1: SCHEDULED -->
            <div v-if="session.status === 0 || session.status === 1">
              <p style="font-size: 15px; font-weight: bold; color: #424242" class="mb-2">
                Notify me before the session:
              </p>
              <v-checkbox v-model="notify.immediately" label="Notify immediately" color="#A6CC39" hide-details
                class="mt-0"></v-checkbox>
              <v-checkbox v-model="notify.oneDay" label="1 day before" color="#A6CC39" hide-details
                class="mt-0"></v-checkbox>
              <v-checkbox v-model="notify.oneHour" label="1 hour before" color="#A6CC39" hide-details
                class="mt-0"></v-checkbox>
              <v-checkbox v-model="notify.tenMinutes" label="10 minutes before" color="#A6CC39" hide-details
                class="mt-0 mb-4"></v-checkbox>

              <!-- Session Full button -->
              <v-btn v-if="isFull && !isEnrolled" depressed disabled class="text-capitalize mt-2"
                style="border-radius: 8px; font-size: 16px">
                <v-icon left>mdi-account-off</v-icon>
                Session Full
              </v-btn>

              <!-- Normal Join/UnJoin button -->
              <v-btn v-else depressed class="text-capitalize white--text mt-2"
                style="border-radius: 8px; font-size: 16px" :color="isEnrolled ? '#008AFF' : '#A6CC39'"
                :loading="enrollLoading" @click="toggleEnroll()">
                <v-icon left>{{ isEnrolled ? "mdi-account-minus" : "mdi-account-plus" }}</v-icon>
                {{ isEnrolled ? "UnJoin" : "Join" }}
              </v-btn>
            </div>

            <!-- STATUS 2: LIVE -->
            <div v-else-if="session.status === 2">
              <v-btn depressed class="text-capitalize white--text" color="#E53935"
                style="border-radius: 8px; font-size: 16px" :loading="joiningLive" @click="joinLiveRoom()">
                <v-icon left>mdi-broadcast</v-icon>
                Join Live
              </v-btn>
            </div>

            <!-- STATUS 3: ENDED -->
            <div v-else-if="session.status === 3">
              <p style="font-size: 15px; font-weight: bold; color: #424242" class="mb-2">
                Session Recordings
              </p>
              <div v-if="recordings.length > 0">
                <v-list dense class="pa-0">
                  <v-list-item v-for="rec in recordings" :key="rec.id" class="px-0">
                    <v-list-item-icon class="mr-2">
                      <v-icon color="#A6CC39">mdi-file-video</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title style="font-size: 14px">
                        {{ rec.fileName }}
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn small depressed color="#A6CC39" class="text-capitalize white--text"
                        style="border-radius: 8px" @click="downloadRecording(rec.filePath)">
                        <v-icon small left>mdi-download</v-icon>
                        Download
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </div>
              <div v-else style="color: #a4a4a4; font-size: 15px">
                No recordings available.
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Feedback snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3500" top right>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- Join-block alert dialog -->
    <v-dialog v-model="alertDialog" max-width="420" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="justify-center pa-5">
          <span style="font-size:17px;font-weight:600;line-height:1.3;text-align:center">{{ alertDialogTitle }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 pb-2" style="font-size:14px;color:#555;text-align:center">
          {{ alertDialogText }}
        </v-card-text>
        <v-card-actions class="pa-4 pt-2 justify-center">
          <v-btn depressed :color="alertDialogColor" dark
            style="border-radius:8px;text-transform:none;font-weight:600;min-width:120px" @click="alertDialog = false">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "LiveSessionDetail",
  data() {
    return {
      loading: true,
      session: {},
      isEnrolled: false,
      recordings: [],
      notify: {
        immediately: false,
        oneDay: false,
        oneHour: false,
        tenMinutes: false,
      },
      enrollLoading: false,
      joiningLive: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "error",
      alertDialog: false,
      alertDialogTitle: "",
      alertDialogText: "",
      alertDialogColor: "error",
      alertDialogIcon: "mdi-alert-circle-outline",
      breadcrumbs: [
        {
          text: "livestreaming",
          href: "/live",
        },
      ],
    };
  },
  computed: {
    isFull() {
      return (
        this.session.noOfParticipants > 0 &&
        this.session.joinedUsers >= this.session.noOfParticipants
      );
    },
  },
  mounted() {
    this.getFavourite();
    this.getNotificationCount();
    this.getSessionDetail();
  },
  methods: {
    async getSessionDetail() {
      const liveSessionID =
        this.$route.params.id || this.$route.query.sessionId;
      const userId = localStorage.getItem("UserID");
      const res = await this.$axios.get(
        `${this.web_url}LiveSession/GetSessionMobileById/${liveSessionID}?userId=${userId}`
      );
      if (res.data.status === 0) {
        const data = res.data.data;
        this.session = {
          ...data,
          status: Number.parseInt(data.status ?? 0),
          noOfParticipants: data.noOfParticipants ?? 0,
          joinedUsers: data.joinedUsers ?? 0,
        };
        this.isEnrolled = data.joinFlag ?? false;
        this.recordings = data.recordings ?? [];
        this.notify.immediately = data.immediately ?? false;
        this.notify.oneDay = data.oneDay ?? false;
        this.notify.oneHour = data.oneHour ?? false;
        this.notify.tenMinutes = data.tenMinutes ?? false;
        this.loading = false;
      }
    },

    async toggleEnroll() {
      this.enrollLoading = true;
      const payload = {
        liveSessionID: this.session.liveSessionID,
        userID: Number.parseInt(localStorage.getItem("UserID")),
        joinLive: !this.isEnrolled,
        immediately: this.notify.immediately,
        oneDay: this.notify.oneDay,
        oneHour: this.notify.oneHour,
        tenMinutes: this.notify.tenMinutes,
      };

      try {
        const res = await this.$axios.post(
          `${this.web_url}LiveSession/UpdateEnrollLive`,
          payload
        );

        if (res.data.status === 0) {
          this.isEnrolled = !this.isEnrolled;
          this.snackbarColor = "success";
          this.snackbarText = this.isEnrolled
            ? "Successfully joined the session!"
            : "You have left the session.";
          this.snackbar = true;
        } else {
          this.snackbarColor = "error";
          this.snackbarText = res.data.message || "Action failed. Please try again.";
          this.snackbar = true;
        }
      } catch (e) {
        console.warn('[LiveSessionDetail] toggleEnroll:', e);
        this.snackbarColor = "error";
        this.snackbarText = "Network error. Please try again.";
        this.snackbar = true;
      } finally {
        this.enrollLoading = false;
      }
    },

    async joinLiveRoom() {
      this.joiningLive = true;
      try {
        const payload = {
          sessionID: this.session.liveSessionID,
          userID: Number.parseInt(localStorage.getItem("UserID")),
          userName: localStorage.getItem("userName"),
        };
        const res = await this.$axios.post(
          `${this.web_url}LiveSession/GetRoomToken`,
          payload
        );
        if (res.data.status === 0) {
          const { token, liveKitUrl, roomID, isAlreadyPublishing } = res.data.data;

          // Block if this user is already publishing (joined from another device)
          if (isAlreadyPublishing) {
            this.alertDialogTitle = "Already in Session";
            this.alertDialogText = "You are already joined in this session from another device. Please leave that session before joining again.";
            this.alertDialogColor = "#A6CC39";
            this.alertDialogIcon = "mdi-monitor-multiple";
            this.alertDialog = true;
            return;
          }

          this.$router.push({
            path: "/live/room/" + roomID,
            query: { token, liveKitUrl, roomID, liveSessionID: this.session.liveSessionID },
          });
        } else {
          this.snackbarColor = "error";
          this.snackbarText = res.data.message || "Unable to join. Please try again.";
          this.snackbar = true;
        }
      } catch (e) {
        console.warn('[LiveSessionDetail] joinLiveRoom:', e);
        this.snackbarColor = "error";
        this.snackbarText = "Network error. Please try again.";
        this.snackbar = true;
      } finally {
        this.joiningLive = false;
      }
    },

    downloadRecording(url) {
      globalThis.open(url, "_blank");
    },

    statusLabel(status) {
      const s = Number.parseInt(status);
      if (s === 0 || s === 1) return "Scheduled";
      if (s === 2) return "LIVE";
      if (s === 3) return "Ended";
      return "Ended";
    },
    statusClass(status) {
      const s = Number.parseInt(status);
      if (s === 0 || s === 1) return "badge--scheduled";
      if (s === 2) return "badge--live";
      if (s === 3) return "badge--ended";
      return "badge--ended";
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const dt = new Date(dateStr);
      const day = dt.getDate();
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[dt.getMonth()];
      const year = dt.getFullYear();
      return `${day} ${month} ${year}`;
    },
    formatTime(dateStr) {
      if (!dateStr) return '';
      const dt = new Date(dateStr);
      return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    },
  },
};
</script>

<style scoped>
.detail-img {
  width: 100%;
  height: 300px;
  object-fit: cover;
}

.detail-img-placeholder {
  width: 100%;
  height: 300px;
  background-color: #f5f5f5;
}

.badge--scheduled {
  background-color: #1976d2 !important;
  color: #fff !important;
}

.badge--live {
  background-color: #b71c1c !important;
  color: #fff !important;
}

.badge--ended {
  background-color: #5e5e5e !important;
  color: #fff !important;
}
</style>
